import { Inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';
import { Observable, map } from 'rxjs';
import { PriceLabelValue } from '../components/UI/price-label/price-label.types';
import { AppCurrency } from '../types/currency';
import { ProductPrice } from '../api/contracts/product';
import { APP_CONFIG, AppConfig } from '../config/app-config';

@Pipe({
  name: 'appCurrency',
})
export class CurrencyPipe implements PipeTransform {
  constructor(
    private readonly translationService: TranslateService,
    private readonly languageService: LanguageService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig
  ) {}

  transform(
    priceObj: ProductPrice | number,
    currency: AppCurrency = this.appConfig.mainCurrency,
  ): Observable<PriceLabelValue> {
    const mainCurrency = this.appConfig.mainCurrency;
    const price =
      typeof priceObj === 'number' ? priceObj : priceObj[mainCurrency];
    return this.languageService.currentLang.pipe(
      map((lang) => {
        const currencyLabel = this.translationService.instant(
          `@common.currencies.${currency}`
        );
        return createPriceLabelValue(price, lang, currency, currencyLabel);
      })
    );
  }
}

function createPriceLabelValue(
  price: number,
  lang: string,
  currency: AppCurrency,
  currencyLabel: string
): PriceLabelValue {
  const priceString = price.toString();
  const priceParts = {
    whole: priceString.slice(0, priceString.length - 2) || '0',
    cents: priceString.slice(priceString.length - 2),
  };
  const langToPriceLabelValueMap: Record<
    typeof lang,
    Record<AppCurrency, PriceLabelValue>
  > = {
    en: {
      [AppCurrency.BGN]: {
        start: currencyLabel,
        bigPart: priceParts.whole,
        end: priceParts.cents,
        splitter: '.',
        asString: () =>
          `${currencyLabel}${priceParts.whole}.${priceParts.cents}`,
      },
      [AppCurrency.EUR]: {
        start: currencyLabel,
        bigPart: priceParts.whole,
        end: priceParts.cents,
        splitter: '.',
        asString: () =>
          `${currencyLabel}${priceParts.whole}.${priceParts.cents}`,
      },
    },
    bg: {
      [AppCurrency.BGN]: {
        start: '',
        bigPart: priceParts.whole,
        end: `${priceParts.cents} ${currencyLabel}`,
        splitter: ',',
        asString: () =>
          `${priceParts.whole},${priceParts.cents}${currencyLabel}`,
      },
      [AppCurrency.EUR]: {
        start: currencyLabel,
        bigPart: priceParts.whole,
        end: priceParts.cents,
        splitter: '.',
        asString: () =>
          `${currencyLabel}${priceParts.whole}.${priceParts.cents}`,
      },
    },
  };
  return langToPriceLabelValueMap[lang][currency];
}

// BGN7.99
// 7,99 лв.
